/* eslint-disable no-nested-ternary */
import React, {
    createContext, FC, ReactNode, useMemo, useReducer, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { TApplicationDetailsManager } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import { dateFormatZurich, isDateExpired } from 'utils/datetime';
import { ApplicationStatus, DocStatus } from 'components/DocBox/DocBox.types';
import { ApplicationTypesEnum } from 'components/ApplicationDetails/Applications/Applications.types';
import { STATUS, THREEBTYPES } from 'constants/constants';
import { useLayout } from 'prodivers/layout';
import { memberProfileSelector } from 'redux-store/auth/authSelectors';
import { useManagerProfile } from 'prodivers/managerProfile';
import { accessPolicies } from 'prodivers/managerProfile/constants';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import arrayContains from 'utils/arrayUtils';
import { initialState } from './constants';
import { reducer } from './ApplicationDetailsManager.reducer';
import { useCommonApplicationDetails } from './strategy/product/useCommonApplicationDetails';
import { useDerivedApplicationDocuments } from './hooks/useDerivedApplicationDocuments';
import { useApplicationsListDerivation } from './hooks/useApplicationsListDerivation';
import { useIdDocuments } from './hooks/useIdDocuments';
import { useDocumentGeneration } from './hooks/useDocumentGeneration';
import { useApplicationSubmission } from './hooks/useApplicationSubmission';
import { useApplicationActions } from './hooks/useApplicationActions';

export const ApplicationDetailsContext = createContext<TApplicationDetailsManager>(null as any);

type TApplicationDetailsManagerProps = {
    children: ReactNode;
    investmentAppsData: any;
    isInvestmentApplicationsLoading: boolean;
    getInvestmentApplications: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>
    is3aVB: boolean;
}

const isZIVVorAZP = ({ externalId }) => arrayContains([THREEBTYPES.zivv, THREEBTYPES.azpNeu, THREEBTYPES.azpNeuSB, THREEBTYPES.azpAlt], externalId);

// eslint-disable-next-line import/no-unused-modules
export const ApplicationDetailsManager: FC<TApplicationDetailsManagerProps> = ({
    children,
    investmentAppsData,
    isInvestmentApplicationsLoading,
    getInvestmentApplications,
    is3aVB: is3aVBApp,
}) => {
    const [apiError, setApiError] = useState<{ message: string } | null>(null);
    const { i18n: { language } } = useTranslation();
    const [state, dispatch] = useReducer(reducer, initialState());
    const profile:any = useSelector(memberProfileSelector);
    const { setLoadingOverlayActive }: any = useLayout();
    const { containerId, applicationId: urlApplicationId, dfsClientId } = useParams();

    const {
        isJA,
        jaUsers,
        generalDocumentsData,
        proofOfAssetsDocsData,
        advisoryData,
        applicationsDocumentsData,
        isAdvisoryCase,
        isApplicationDocsLoading,
        isGetGeneralDocumentsLoading,
        isProofOfAssetsDocsDataLoading,
        documentDataForOldAdvisory,
        showGeneralDocumentsAndProofOfAssetsSection,
        refetchNon3bApplicationsDocuments,
        advisoryDetailsData,
        refetchAdvisory,
    } = useCommonApplicationDetails({ is3aVb: is3aVBApp, investmentAppsData });

    // Enabled-driven. The `refetch` handle is kept: useApplicationSubmission calls it in
    // refreshAfterSubmission, and the sign+poll path carries no invalidateQueryKeys, so that manual
    // refetch is load-bearing. `refetchOnWindowFocus:false` preserves the prior no-focus behaviour.
    const {
        data: casesByContainerId,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getCasesByContainerId',
        payload: { containerId },
        enabled: !!containerId && is3aVBApp === false,
        refetchOnWindowFocus: false,
        staleTime: 3 * 1000, // 3 seconds
        gcTime: 3 * 1000, // 3 seconds
    });

    // Enabled-driven; no refetch handle needed (it was only used for the initial load). Shares its
    // cache key with the Cases page's own useGetCustomService call for the same api.
    const {
        data: listOfEmptyFormDocuments,
        isLoading: listOfEmptyFormDocumentsLoading,
    } = useGetCustomService({
        service: 'customCaseManagementServices',
        api: 'getListOfEmptyFormDocuments',
        payload: { language },
        enabled: !!language && is3aVBApp === false,
        refetchOnWindowFocus: false,
    });

    const { hasAccessPolicy } = useManagerProfile();
    const hasDocumentESignAccess = useMemo(() => hasAccessPolicy(accessPolicies.EnableDocumentSigning), [hasAccessPolicy]);
    const canCompleteFollowUpInvestorProfileApplication = useMemo(() => hasAccessPolicy(accessPolicies.CompleteFollowUpInvestorProfileApplication), [hasAccessPolicy]);
    const hasAccessToReUpload = useMemo(() => hasAccessPolicy(accessPolicies.SubmitApplicationWithUploadedAdvisory), [hasAccessPolicy]);

    const isAnyApplicationSubmitted = useMemo(() => state?.applicationsData?.some(app => app.status === STATUS.submitted), [state?.applicationsData]);
    const isContainerWithZIVVorAZP = useMemo(() => state?.applicationsData?.some(app => isZIVVorAZP({ externalId: app?.productExternalId }) && ![ApplicationTypesEnum.SELL, ApplicationTypesEnum.BUY]?.includes(app?.typeKey)), [state?.applicationsData]);
    const isUploadedAdvisory = useMemo(() => advisoryDetailsData?.system != null && (advisoryDetailsData?.system !== 3), [advisoryDetailsData?.system]);
    const canReUploadAdvisoryDocs = useMemo(() => isContainerWithZIVVorAZP && isUploadedAdvisory && !isAnyApplicationSubmitted && hasAccessToReUpload, [isUploadedAdvisory, isContainerWithZIVVorAZP, isAnyApplicationSubmitted, hasAccessToReUpload]);
    const showCanNotSubmitApplicationMessage = useMemo(() => isContainerWithZIVVorAZP && !!isUploadedAdvisory && !isAnyApplicationSubmitted, [isUploadedAdvisory, isContainerWithZIVVorAZP, isAnyApplicationSubmitted]);
    const isAllApplicationCancellable = useMemo(
        () => !investmentAppsData?.some(app => (
            [
                ApplicationStatus.canceled,
                ApplicationStatus.revoked,
                ApplicationStatus.submitted,
                ApplicationStatus.completed,
                ApplicationStatus.closed,
                ApplicationStatus.expired,
            ].includes(app.status)
        && !app?.investmentDescription?.changeInvestmentAmount
        )),
        [investmentAppsData],
    );

    const isAdvisoryExpired = useMemo(() => isDateExpired(advisoryData?.validTill, dateFormatZurich), [advisoryData?.validTill]);
    const applicationsQuantity = investmentAppsData?.length;
    const isFollowUpInvestorProfileCase = useMemo(() => investmentAppsData?.some(application => application?.investmentDescription?.changeInvestmentAmount?.isFollowUpInvestorProfile), [investmentAppsData]);

    // Derive proof-of-assets / basic-agreement documents + the advisory document into the reducer.
    const { advisoryDocument } = useDerivedApplicationDocuments({
        dispatch,
        generalDocumentsData,
        proofOfAssetsDocsData,
        documentDataForOldAdvisory,
        isJA,
        isProofOfAssetsDocsDataLoading,
        isGetGeneralDocumentsLoading,
        language,
    });

    // Derive the normalized applications list into the reducer.
    useApplicationsListDerivation({
        dispatch,
        investmentAppsData,
        applicationsDocumentsData,
        casesByContainerId,
        isAdvisoryCase,
        applicationsQuantity,
    });

    const { onIdDocumentUpload } = useIdDocuments({ dispatch, jaUsers });

    const {
        onDocGeneration,
        onDocGenerationWithNewAPI,
        setRetryState,
        is3BDocGenerationInProgress,
    } = useDocumentGeneration({ dispatch, refetchNon3bApplicationsDocuments, dfsClientId: dfsClientId as string });

    const {
        submitApplication,
        advisoryDocSubmittedForReview,
        setAdvisoryDocSubmittedForReview,
    } = useApplicationSubmission({
        dispatch,
        setApiError,
        setLoadingOverlayActive,
        profile,
        showCanNotSubmitApplicationMessage,
        hasAccessToReUpload,
        isFollowUpInvestorProfileCase,
        canCompleteFollowUpInvestorProfileApplication,
    });

    const {
        cancelAllApplications,
        cancelApplication,
        acceptDocumentSend,
        onESign,
        onManualSign,
        onAdvisoryDocReUpload,
    } = useApplicationActions({
        dispatch,
        containerId,
        urlApplicationId,
        dfsClientId,
        applicationsData: state.applicationsData,
        advisoryDetailsData,
        getInvestmentApplications,
        refetchAdvisory,
    });

    const isAdvisoryDocReadyForSubmission = useMemo(() => (
        !advisoryData || !(
            state.basicAgreementDocuments.some((item) => item.status !== DocStatus.ReadyToSubmit && item.status !== DocStatus.Disabled)
            || state.proofOfAssetDocuments.some((item) => item.status !== DocStatus.ReadyToSubmit && item.status !== DocStatus.Disabled)
        )
    ), [state.basicAgreementDocuments, state.proofOfAssetDocuments, isAdvisoryCase]);

    const isApplicationsLoading = useMemo(() => isInvestmentApplicationsLoading || isApplicationDocsLoading, [isInvestmentApplicationsLoading, isApplicationDocsLoading]);
    const emptyFormDocuments = useMemo(() => listOfEmptyFormDocuments?.data?.documents ?? [], [listOfEmptyFormDocumentsLoading, listOfEmptyFormDocuments?.data?.documents]);
    const isAllIdDocumentsSigned = useMemo(() => state?.idDocuments?.every(d => d.status === DocStatus.ReadyToSubmit), [state?.idDocuments]);

    const value = useMemo(() => ({
        isJA,
        jaUsers,
        advisoryData,
        advisoryDocument,
        isAdvisoryExpired,
        submitApplication,
        cancelApplication,
        cancelAllApplications,
        acceptDocumentSend,
        isAdvisoryCase,
        isWithdrawalPlanChange: false,
        onManualSign,
        onESign,
        onDocGeneration,
        onDocGenerationWithNewAPI,
        setRetryState,
        isAdvisoryDocReadyForSubmission,
        isAllApplicationCancellable,
        isApplicationsLoading,
        showGeneralDocumentsAndProofOfAssetsSection,
        hasDocumentESignAccess,
        emptyFormDocuments,
        onIdDocumentUpload,
        isAllIdDocumentsSigned,
        apiError,
        isFollowUpInvestorProfileCase,
        canReUploadAdvisoryDocs,
        showCanNotSubmitApplicationMessage,
        advisoryDocSubmittedForReview,
        canCompleteFollowUpInvestorProfileApplication,
        setAdvisoryDocSubmittedForReview,
        hasAccessToReUpload,
        onAdvisoryDocReUpload,
        is3BDocGenerationInProgress,
        isUploadedAdvisory,
        ...state,
    }), [
        isJA, jaUsers, advisoryData, advisoryDocument, isAdvisoryExpired,
        submitApplication, cancelApplication, cancelAllApplications, acceptDocumentSend,
        isAdvisoryCase, isApplicationsLoading,
        onManualSign, onESign, onDocGeneration, onDocGenerationWithNewAPI, isAllApplicationCancellable,
        setRetryState, isAdvisoryDocReadyForSubmission, state, hasDocumentESignAccess, emptyFormDocuments,
        showGeneralDocumentsAndProofOfAssetsSection, onIdDocumentUpload, isAllIdDocumentsSigned,
        apiError, isFollowUpInvestorProfileCase, canReUploadAdvisoryDocs, showCanNotSubmitApplicationMessage,
        advisoryDocSubmittedForReview, canCompleteFollowUpInvestorProfileApplication, setAdvisoryDocSubmittedForReview,
        hasAccessToReUpload, onAdvisoryDocReUpload,
        is3BDocGenerationInProgress,
        isUploadedAdvisory,
    ]);

    return (
        <ApplicationDetailsContext.Provider value={value}>
            {children}
        </ApplicationDetailsContext.Provider>
    );
};
